// EstadoObraBadge / EstadoRedesBadge — chips de estado sobre reui/badge
// (plan_migracion_reui.md §2.h.2), «radius=full» con punto, composición
// TAL CUAL c-badge-22
// (https://reui.io/preview/base/components/c-badge-22?ref=mcp): un
// <span> de currentColor a modo de punto + el texto. Sin estilo propio;
// los mapas de variante viven en `./constants.ts`
// (VARIANTE_ESTADO_OBRA, VARIANTE_ESTADO_REDES).
//
// Ajuste 15 (R-21/C-12): siempre `size="default"` (el `sm` mide 10 px) y
// NUNCA una variante sólida de estado (`success|warning|info|destructive`
// a secas dan texto blanco sobre los pasteles de dark) — solo `-light` /
// `-outline`.
import { Badge, type BadgeProps } from '@/components/reui/badge';
import { cn } from '@/lib/utils';
import { VARIANTE_ESTADO_OBRA, VARIANTE_ESTADO_OBRA_DEFAULT, VARIANTE_ESTADO_REDES, VARIANTE_ESTADO_REDES_DEFAULT } from './constants';

interface EstadoBadgeProps {
  estado: string;
  className?: string;
}

// Punto de c-badge-22: currentColor, así hereda el color del texto de cada
// variante sin declarar uno propio por estado.
function PuntoEstado() {
  return <span aria-hidden="true" className="ms-0.25 size-1.25 rounded-full! bg-[currentColor]" />;
}

export function EstadoObraBadge({ estado, className }: EstadoBadgeProps) {
  const variant: BadgeProps['variant'] = VARIANTE_ESTADO_OBRA[estado] ?? VARIANTE_ESTADO_OBRA_DEFAULT;
  return (
    <Badge variant={variant} size="default" radius="full" className={cn(className)}>
      <PuntoEstado /> {estado}
    </Badge>
  );
}

interface EstadoRedesBadgeProps extends EstadoBadgeProps {
  /** Texto visible, si es distinto de `estado` (p. ej. «Esp. Definitiva» en
   *  vez de «Esperando Definitiva», artboard `Home.dc.html` `rdtTxt` —
   *  hallazgo #4 vista, R4a). `estado` SIGUE siendo la clave del lookup de
   *  variante: esta prop no cambia el color, solo el texto. Con `etiqueta`,
   *  el texto abreviado queda `aria-hidden` y `estado` (el completo) pasa a
   *  un `sr-only` — quien use un lector de pantalla lee el estado entero
   *  igual; quien lo pasa (obra-card.tsx) es responsable de ofrecer también
   *  un Tooltip con el texto completo para el mouse. Sin esta prop, el
   *  render es idéntico al de antes (un solo nodo de texto, sin spans
   *  extra). */
  etiqueta?: string;
  /** Prefijo «RDT» ADENTRO de la píldora, antes del punto/texto — decisión
   *  de Juan (V2, 28/9: adentro, como la rc.1;
   *  C:/Dev/RedeApp-base-rc1/frontend/src/components/obras/obra-card.tsx:229-240,
   *  rdt/header-obra.tsx:86-95, obras/estado-redes-dialog.tsx:268-277).
   *  Antes cada consumidor lo dibujaba como texto suelto AFUERA del Badge;
   *  ahora vive en un solo lugar, con el mismo estilo de la rc.1 (9 px,
   *  bold, tracking 0.06em, text-tertiary) y el `gap-1` propio del tamaño
   *  `default` del Badge lo separa del punto sin clases extra. */
  prefijo?: string;
}

export function EstadoRedesBadge({ estado, etiqueta, prefijo, className }: EstadoRedesBadgeProps) {
  const variant: BadgeProps['variant'] = VARIANTE_ESTADO_REDES[estado] ?? VARIANTE_ESTADO_REDES_DEFAULT;
  // Archivado: el mismo tratamiento neutro «apagado» que ya tenía el chip a
  // mano (border-dashed + text-tertiary) — la única adaptación de token
  // fuera del mapa de variantes, porque no hay una variante de Badge que la
  // exprese sola.
  return (
    <Badge
      variant={variant}
      size="default"
      radius="full"
      className={cn(estado === 'Archivado' && 'border-dashed text-text-tertiary', className)}
    >
      {prefijo != null && (
        // Sin aria-hidden — igual que en la rc.1: es texto real, lo lee el
        // lector de pantalla antes del estado (completo o abreviado).
        <span className="flex-none text-[9px] font-bold tracking-[0.06em] text-text-tertiary">{prefijo}</span>
      )}
      <PuntoEstado />{' '}
      {etiqueta != null ? (
        <>
          <span aria-hidden="true">{etiqueta}</span>
          <span className="sr-only">{estado}</span>
        </>
      ) : (
        estado
      )}
    </Badge>
  );
}
